import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  Psychology,
  Send,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';

export interface CustomerFormData {
  upload_id: string;
  company_name: string;
  contact_email: string;
  contact_phone: string;
  industry: string;
  customer_type: string;
  quantity_category: string;
  product_type: string;
  how_found: string;
  notes: string;
  consent_given: boolean;
}

export interface AIQualificationResult {
  score: number; // 0-100
  category: 'high_potential' | 'medium_potential' | 'low_potential' | 'not_qualified';
  reasoning: string[];
  recommendations: string[];
  priority: 'urgent' | 'high' | 'medium' | 'low';
}

export interface CustomerFormSubmissionData extends CustomerFormData {
  aiQualification?: AIQualificationResult | null;
}

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CustomerFormSubmissionData) => Promise<void>;
  uploadId: string;
  analysisResults?: any;
  submitting?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  open,
  onClose,
  onSubmit,
  uploadId,
  analysisResults,
  submitting = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [aiQualification, setAiQualification] = useState<AIQualificationResult | null>(null);
  const [qualificationLoading, setQualificationLoading] = useState(false);

  const [formData, setFormData] = useState<CustomerFormData>({
    upload_id: uploadId,
    company_name: '',
    contact_email: '',
    contact_phone: '',
    industry: '',
    customer_type: '',
    quantity_category: '',
    product_type: '',
    how_found: '',
    notes: '',
    consent_given: false
  });

  const steps = ['Kontaktdaten', 'Unternehmensdetails', 'Bestätigung'];

  // AI-Vorbewertung basierend auf Formular und Analyseergebnissen
  const performAIQualification = async () => {
    setQualificationLoading(true);

    try {
      // Import leadService dynamically to avoid circular dependencies
      const { leadService } = await import('../services/leadService');

      // Use real AI analysis
      const qualification = await leadService.getAIQualification(formData, analysisResults);
      setAiQualification(qualification);

    } catch (error) {
      console.error('AI Qualification failed:', error);

      // Fallback to local calculation
      const qualification = calculateAIQualification(formData, analysisResults);
      setAiQualification(qualification);
    } finally {
      setQualificationLoading(false);
    }
  };

  // AI-Vorbewertungslogik (vereinfacht - könnte durch echte AI ersetzt werden)
  const calculateAIQualification = (formData: CustomerFormData, analysisResults: any): AIQualificationResult => {
    let score = 0;
    const reasoning: string[] = [];
    const recommendations: string[] = [];

    // Bewertung basierend auf Unternehmensdaten
    if (formData.company_name.length > 5) {
      score += 15;
      reasoning.push("Etabliertes Unternehmen mit aussagekräftigem Namen");
    }

    // Industry-basierte Bewertung
    const highValueIndustries = ['Manufacturing', 'Automotive', 'Energy', 'Healthcare', 'Industrial'];
    if (highValueIndustries.includes(formData.industry)) {
      score += 25;
      reasoning.push(`${formData.industry}: Branche mit hohem Hybrid-Speicher-Potenzial`);
    }

    // Customer Type Bewertung
    if (formData.customer_type === 'B2B') {
      score += 20;
      reasoning.push("B2B-Kunde: Höhere Wahrscheinlichkeit für größere Projekte");
    }

    // Analyseergebnis-basierte Bewertung
    if (analysisResults?.score?.level === 'GREEN') {
      score += 30;
      reasoning.push("Analyseergebnisse zeigen optimale Eignung für Hybrid-Speicher");
      recommendations.push("Sofortige Kontaktaufnahme empfohlen - Idealer Kunde");
    } else if (analysisResults?.score?.level === 'YELLOW') {
      score += 15;
      reasoning.push("Analyseergebnisse zeigen Potenzial für Hybrid-Speicher");
      recommendations.push("Detailberatung anbieten - Gutes Potenzial");
    } else {
      score += 5;
      reasoning.push("Analyseergebnisse zeigen begrenzte Eignung");
      recommendations.push("Aufklärung über Optimierungsmöglichkeiten");
    }

    // Quantity Category
    const quantities = {
      'Großserie': 25,
      'Kleinserie': 15,
      'Prototyp': 10,
      'Einzelstück': 5
    };
    const quantityScore = quantities[formData.quantity_category as keyof typeof quantities] || 0;
    score += quantityScore;

    if (quantityScore > 15) {
      reasoning.push("Große Abnahmemenge indiziert");
    }

    // Determination von category und priority
    let category: AIQualificationResult['category'];
    let priority: AIQualificationResult['priority'];

    if (score >= 80) {
      category = 'high_potential';
      priority = 'urgent';
      recommendations.push("Sofortige Bearbeitung innerhalb 2 Stunden");
    } else if (score >= 60) {
      category = 'medium_potential';
      priority = 'high';
      recommendations.push("Bearbeitung innerhalb 24 Stunden");
    } else if (score >= 40) {
      category = 'low_potential';
      priority = 'medium';
      recommendations.push("Bearbeitung innerhalb 3 Tagen");
    } else {
      category = 'not_qualified';
      priority = 'low';
      recommendations.push("Standard-Bearbeitung, Potenzial prüfen");
    }

    return {
      score: Math.min(100, score),
      category,
      reasoning,
      recommendations,
      priority
    };
  };

  const handleInputChange = (field: keyof CustomerFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      // After "Unternehmensdetails" step, run AI-Qualification silently in background
      // Don't await this - let it run while user sees the final confirmation step
      performAIQualification();
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      // Include AI qualification data in the submission
      const submissionData = {
        ...formData,
        aiQualification: aiQualification
      };
      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const getAIResultColor = (category: string) => {
    switch (category) {
      case 'high_potential': return 'success';
      case 'medium_potential': return 'warning';
      case 'low_potential': return 'info';
      default: return 'error';
    }
  };

  const getAIResultIcon = (category: string) => {
    switch (category) {
      case 'high_potential': return <CheckCircle />;
      case 'medium_potential': return <Warning />;
      case 'low_potential': return <Info />;
      default: return <Warning />;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Kontaktdaten
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Unternehmen"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-Mail Adresse"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefonnummer (optional)"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
        );

      case 1: // Unternehmensdetails
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Branche</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                >
                  <MenuItem value="Automotive">Automotive</MenuItem>
                  <MenuItem value="Industrial">Industrial</MenuItem>
                  <MenuItem value="Energy">Energie</MenuItem>
                  <MenuItem value="Technology">Technologie</MenuItem>
                  <MenuItem value="Manufacturing">Produktion/Fertigung</MenuItem>
                  <MenuItem value="Healthcare">Gesundheitswesen</MenuItem>
                  <MenuItem value="Aerospace">Luft- und Raumfahrt</MenuItem>
                  <MenuItem value="Marine">Marine</MenuItem>
                  <MenuItem value="Telecommunications">Telekommunikation</MenuItem>
                  <MenuItem value="Other">Sonstige</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kundentyp</InputLabel>
                <Select
                  value={formData.customer_type}
                  onChange={(e) => handleInputChange('customer_type', e.target.value)}
                >
                  <MenuItem value="B2B">B2B</MenuItem>
                  <MenuItem value="B2C">B2C</MenuItem>
                  <MenuItem value="Institution">Institution/Behörde</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Projektgröße</InputLabel>
                <Select
                  value={formData.quantity_category}
                  onChange={(e) => handleInputChange('quantity_category', e.target.value)}
                >
                  <MenuItem value="Großserie">Großserie</MenuItem>
                  <MenuItem value="Kleinserie">Kleinserie</MenuItem>
                  <MenuItem value="Prototyp">Prototyp</MenuItem>
                  <MenuItem value="Einzelstück">Einzelstück</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Anwendungsbereich</InputLabel>
                <Select
                  value={formData.product_type}
                  onChange={(e) => handleInputChange('product_type', e.target.value)}
                >
                  <MenuItem value="Serienprodukt">Serienprodukt</MenuItem>
                  <MenuItem value="Prototyp">Prototyp</MenuItem>
                  <MenuItem value="Pilotanlage">Pilotanlage</MenuItem>
                  <MenuItem value="Sonderanfertigung">Sonderanfertigung</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Wie haben Sie uns gefunden?</InputLabel>
                <Select
                  value={formData.how_found}
                  onChange={(e) => handleInputChange('how_found', e.target.value)}
                >
                  <MenuItem value="Google Search">Google Search</MenuItem>
                  <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                  <MenuItem value="Referral">Empfehlung</MenuItem>
                  <MenuItem value="Trade Show">Messe</MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Advertisement">Werbung</MenuItem>
                  <MenuItem value="Partner">Partner</MenuItem>
                  <MenuItem value="Other">Sonstiges</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zusätzliche Anmerkungen"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 2: // Bestätigung
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Anfrage bereit zum Versenden
              </Typography>
              <Typography>
                Ihre Daten werden sicher übertragen und DSGVO-konform verarbeitet.
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.consent_given}
                  onChange={(e) => handleInputChange('consent_given', e.target.checked)}
                />
              }
              label="Ich stimme der Verarbeitung meiner Daten für die Kontaktaufnahme zu (erforderlich)"
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.company_name && formData.contact_email;
      case 1:
        return formData.industry && formData.customer_type;
      case 2:
        return formData.consent_given;
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Typography variant="h5">
          Kontaktanfrage stellen
        </Typography>
        <Typography color="text.secondary">
          Lassen Sie sich von unseren Experten beraten
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          Abbrechen
        </Button>

        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Zurück
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid() || qualificationLoading}
          >
            Weiter
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isStepValid() || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
          >
            {submitting ? 'Wird gesendet...' : 'Anfrage senden'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerForm;